import type {
  ActivityCreateInput,
  ActivityDto,
  CheckinCreateInput,
  GroupDto,
  PaymentMethod,
  PaymentStatus,
  RollCallCreateInput,
  StudentDto,
} from '@hudhuria/shared';
import { type Mutation, useMutationState } from '@tanstack/react-query';

/**
 * Writes are queued as TanStack mutations (persisted to IndexedDB while pending).
 * Screens render server data merged with these pending writes, so everything a staff
 * member does offline shows up immediately and disappears from the overlay once synced.
 */

export interface CheckinVars {
  input: CheckinCreateInput;
  local: { feeMinor: number; status: PaymentStatus; staffId: string; localDate: string; studentName: string };
}
export interface RollCallVars {
  input: RollCallCreateInput;
  local: { total: number; feeMinor: number; staffId: string; localDate: string; groupName: string };
}
export interface RegisterVars {
  input: {
    id: string;
    centerId: string;
    name: string;
    school: string;
    guardianName: string;
    guardianPhone: string;
    createdAt: string;
  };
  local: { provisionalRef: string; localDate: string };
}
export interface ActivityVars {
  input: ActivityCreateInput;
}
export interface GroupVars {
  input: {
    id: string;
    centerId: string;
    name: string;
    type: GroupDto['type'];
    studentIds: string[];
    teacherIds: string[];
  };
}
export interface PhotoVars {
  studentId: string;
  dataUrl: string;
}
export interface SettleVars {
  paymentId: string;
  studentId: string;
  method: PaymentMethod;
}

const pendingVars = <T>(key: string) =>
  ({
    filters: { mutationKey: [key], status: 'pending' as const },
    select: (m: Mutation<unknown, Error, unknown, unknown>) => m.state.variables as T,
  }) as const;

export const usePendingCheckins = () => useMutationState(pendingVars<CheckinVars>('checkin'));
export const usePendingRollCalls = () => useMutationState(pendingVars<RollCallVars>('rollcall'));
export const usePendingPhotos = () => useMutationState(pendingVars<PhotoVars>('photo'));
export const usePendingSettles = () => useMutationState(pendingVars<SettleVars>('settle'));

export function usePendingRegistrations(): (StudentDto & { localDate: string })[] {
  const regs = useMutationState(pendingVars<RegisterVars>('register'));
  const photos = usePendingPhotos();
  return regs.map((r) => ({
    id: r.input.id,
    ref: r.local.provisionalRef,
    name: r.input.name,
    school: r.input.school || '—',
    guardianName: r.input.guardianName || '—',
    guardianPhone: r.input.guardianPhone || '—',
    homeCenterId: r.input.centerId,
    photoUrl: photos.find((p) => p.studentId === r.input.id)?.dataUrl ?? null,
    visits: 0,
    paidEvents: 0,
    ltvMinor: 0,
    owes: false,
    createdAt: r.input.createdAt,
    pending: true,
    localDate: r.local.localDate,
  }));
}

export function usePendingActivities(centerId: string): ActivityDto[] {
  return useMutationState(pendingVars<ActivityVars>('activity'))
    .filter((a) => a.input.centerId === centerId)
    .map((a) => ({ ...a.input, booked: 0, builtin: false }));
}

export function usePendingGroups(centerId: string): GroupDto[] {
  return useMutationState(pendingVars<GroupVars>('group'))
    .filter((g) => g.input.centerId === centerId)
    .map((g) => ({ ...g.input }));
}

/** Count of writes waiting to sync (for the Hub sync pill). */
export function usePendingCount() {
  return useMutationState({ filters: { status: 'pending' }, select: (m) => m.mutationId }).length;
}
